/**
 * Staff view of the phone AI assistant's records: acute cases and
 * change/cancel requests from voice_* tables, grouped by status with a
 * NEW → IN_PROGRESS → DONE workflow, plus the day's call log ("Hovory")
 * with each call's transcript in a dialog. Reached from the staff home; uses the
 * same Josenika portal design as the home page (portal-shell fragments).
 */

import type { AppRouter } from '@dentaly/server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import {
	ArrowLeft,
	ChevronLeft,
	ChevronRight,
	Clock,
	FileText,
	Phone,
	PhoneIncoming,
	PhoneOutgoing,
	User,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
	CallTranscriptDialog,
	formatDuration,
} from '@/components/dialogs/CallTranscriptDialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDateCzech } from '@/lib/calendar-utils'
import {
	PortalCentered,
	PortalLoading,
	PortalShell,
	portalButtonOutline,
	portalHeading,
	portalLabel,
	portalPanel,
} from '@/routes/appointments/portal-shell'
import { trpc } from '@/trpc/client'

// Inferred from the router so the shapes can never drift from the server
type VoiceAgentList = inferRouterOutputs<AppRouter>['voiceAgent']['list']
type AcuteCase = VoiceAgentList['acuteCases'][number]
type ChangeRequest = VoiceAgentList['changeRequests'][number]
type VoiceStatus = AcuteCase['status']
type CallList = inferRouterOutputs<AppRouter>['voiceAgent']['calls']['list']
type CallItem = CallList['items'][number]
type ShowTranscript = (voiceCallId: string) => void

// typ / problem / zavaznost / strana are free-form strings from the voice
// agent, shown verbatim — only status still has fixed values.
const STATUS_LABELS: Record<VoiceStatus, string> = {
	new: 'Nové',
	in_progress: 'Vyřizuje se',
	done: 'Hotovo',
}

function formatCreatedAt(iso: string): string {
	const date = new Date(iso)
	const hours = date.getHours().toString().padStart(2, '0')
	const minutes = date.getMinutes().toString().padStart(2, '0')
	return `${formatDateCzech(date, 'd. M. yyyy')} ${hours}:${minutes}`
}

/** 777123456 → 777 123 456 (spacing used across the app for CZ numbers). */
function formatPhone(telefon: string): string {
	return telefon.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
}

function toothDetails(item: AcuteCase): string | null {
	const parts = [
		item.strana,
		item.poziceZubu ? `zub ${item.poziceZubu}` : null,
	].filter(Boolean)
	return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Tab trigger in the portal design language: square, uppercase, gold when
 * active — same vocabulary as portalButton/portalButtonOutline.
 */
const voiceTab =
	'rounded-none border-2 border-[#d7ad6c] bg-transparent px-4 py-2.5 text-[13px] font-normal uppercase tracking-[1px] text-[#242424] shadow-none data-[state=active]:bg-[#d7ad6c] data-[state=active]:text-white data-[state=active]:shadow-none'

function openCount<T extends { status: VoiceStatus }>(items: T[]): number {
	return items.filter((i) => i.status !== 'done').length
}

/** Unresolved-items count shown inside a tab label; hidden at zero. */
function TabCount({ count }: { count: number }) {
	if (count === 0) return null
	return (
		<span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center bg-white/90 px-1 text-[11px] font-semibold text-[#d7ad6c]">
			{count}
		</span>
	)
}

function useUpdateStatus() {
	const utils = trpc.useUtils()
	return trpc.voiceAgent.updateStatus.useMutation({
		onSuccess: () => {
			utils.voiceAgent.list.invalidate()
			utils.voiceAgent.newCount.invalidate()
		},
		onError: () => {
			toast.error('Změna stavu se nezdařila. Zkuste to prosím znovu.')
		},
	})
}

function StatusActions({
	kind,
	id,
	status,
	children,
}: {
	kind: 'acute' | 'change'
	id: string
	status: VoiceStatus
	/** Extra actions shown after the status buttons (transcript). */
	children?: React.ReactNode
}) {
	const updateStatus = useUpdateStatus()
	const move = (next: VoiceStatus) =>
		updateStatus.mutate({ kind, id, status: next })

	return (
		<div className="mt-3 flex flex-wrap gap-2">
			{status === 'new' && (
				<Button
					className={`${portalButtonOutline} h-8 px-3 text-[11px]`}
					size="sm"
					disabled={updateStatus.isPending}
					onClick={() => move('in_progress')}
				>
					Začít vyřizovat
				</Button>
			)}
			{status !== 'done' && (
				<Button
					className={`${portalButtonOutline} h-8 px-3 text-[11px]`}
					size="sm"
					disabled={updateStatus.isPending}
					onClick={() => move('done')}
				>
					Hotovo
				</Button>
			)}
			{status !== 'new' && (
				<Button
					className={`${portalButtonOutline} h-8 px-3 text-[11px]`}
					size="sm"
					disabled={updateStatus.isPending}
					onClick={() => move('new')}
				>
					Vrátit mezi nové
				</Button>
			)}
			{children}
		</div>
	)
}

/** Status tag on a card — gold for Nové, dark for Vyřizuje se, gray for Hotovo. */
function StatusTag({ status }: { status: VoiceStatus }) {
	const tone =
		status === 'new'
			? 'border-[#d7ad6c] text-[#d7ad6c]'
			: status === 'in_progress'
				? 'border-[#242424] text-[#242424]'
				: 'border-[#919194] text-[#919194]'
	return (
		<span
			className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[1px] ${tone}`}
		>
			{STATUS_LABELS[status]}
		</span>
	)
}

function ItemHeader({
	title,
	status,
	urgent,
	tag,
}: {
	title: string
	status: VoiceStatus
	urgent?: boolean
	tag?: string
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<p className={`${portalHeading} text-base leading-snug`}>{title}</p>
			{urgent && (
				<span className="border border-red-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[1px] text-red-600">
					Urgentní
				</span>
			)}
			{tag && (
				<span className="border border-[#d7ad6c] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[1px] text-[#d7ad6c]">
					{tag}
				</span>
			)}
			<span className="ml-auto">
				<StatusTag status={status} />
			</span>
		</div>
	)
}

function ItemMeta({
	telefon,
	createdAt,
	patient,
}: {
	telefon: string
	createdAt: string
	patient: AcuteCase['patient']
}) {
	return (
		<div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-base">
			<a
				className="flex items-center gap-2 hover:text-[#d7ad6c]"
				href={`tel:+420${telefon}`}
			>
				<Phone className="h-4 w-4 text-[#d7ad6c]" />
				{formatPhone(telefon)}
			</a>
			<span className="flex items-center gap-2">
				<Clock className="h-4 w-4 text-[#d7ad6c]" />
				{formatCreatedAt(createdAt)}
			</span>
			{patient && (
				<Link
					className="flex items-center gap-2 underline underline-offset-2 hover:text-[#d7ad6c]"
					to={`/patient/${patient.id}`}
				>
					<User className="h-4 w-4 text-[#d7ad6c]" />
					Karta pacienta ({patient.firstName} {patient.lastName})
				</Link>
			)}
		</div>
	)
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
	if (!value) return null
	return (
		<p className="text-base">
			<span className="text-[#919194]">{label}: </span>
			{value}
		</p>
	)
}

/**
 * Opens the call's transcript dialog. Disabled (not hidden) when the call
 * has none, so staff can tell "no transcript" from "no call".
 */
function TranscriptButton({
	voiceCallId,
	hasTranscript,
	onShow,
}: {
	voiceCallId: string | null
	hasTranscript: boolean
	onShow: ShowTranscript
}) {
	if (!voiceCallId) return null
	return (
		<Button
			className={`${portalButtonOutline} h-8 px-3 text-[11px]`}
			size="sm"
			disabled={!hasTranscript}
			title={hasTranscript ? 'Zobrazit přepis' : 'Přepis není k dispozici'}
			aria-label="Zobrazit přepis hovoru"
			onClick={() => onShow(voiceCallId)}
		>
			<FileText className="mr-1.5 h-4 w-4" />
			Přepis
		</Button>
	)
}

/**
 * Body fallback for a record where the agent captured nothing beyond the
 * header fields (typ / name / phone). Without it the card body is empty,
 * which reads to staff as "the details failed to load" rather than
 * "the caller gave none" — the exact ambiguity that hid a stale client
 * build once already.
 */
function NoDetails() {
	return (
		<p className="text-base text-[#919194]">
			Agent nezaznamenal další podrobnosti — zavolejte pacientovi zpět.
		</p>
	)
}

/**
 * Notes the surgery wrote in the ZUB-calendar drawer, read-only here — the
 * drawer stays the only place they are written, but reception must see the
 * same thread whichever screen it is looking at. Hidden when empty so
 * untouched cards keep their current shape.
 */
function DoctorNotes({ notes }: { notes: AcuteCase['notes'] }) {
	if (notes.length === 0) return null
	return (
		<div className="mt-3 border-t border-[#dedede] pt-3">
			<p className={`${portalLabel} mb-2 text-[11px]`}>Poznámky ordinace</p>
			<ul className="space-y-2">
				{notes.map((note) => (
					<li key={note.id} className="border-l-2 border-[#d7ad6c] pl-3">
						<p className="text-[11px] uppercase tracking-[1px] text-[#919194]">
							{note.authorRole === 'ordinace' ? 'Ordinace' : 'Recepce'} ·{' '}
							{formatCreatedAt(note.createdAt)}
						</p>
						{/* whitespace-pre-wrap: the doctor's line breaks are meaningful */}
						<p className="whitespace-pre-wrap break-words text-base">
							{note.text}
						</p>
					</li>
				))}
			</ul>
		</div>
	)
}

function AcuteCaseItem({
	item,
	onShowTranscript,
}: {
	item: AcuteCase
	onShowTranscript: ShowTranscript
}) {
	const kde = toothDetails(item)
	const hasDetails = Boolean(
		item.problem ||
			kde ||
			item.trvani ||
			item.citlivost ||
			item.zavaznost ||
			item.leky ||
			item.poznamka,
	)
	return (
		<div
			className={`${portalPanel} p-5 ${
				item.urgent && item.status !== 'done'
					? 'border-l-4 border-l-red-600'
					: ''
			}`}
		>
			<ItemHeader
				title={`${item.jmeno} ${item.prijmeni}`}
				status={item.status}
				urgent={item.urgent}
			/>
			<ItemMeta
				telefon={item.telefon}
				createdAt={item.createdAt}
				patient={item.patient}
			/>
			<div className="mt-2 space-y-0.5">
				<DetailRow label="Problém" value={item.problem} />
				<DetailRow label="Kde" value={kde} />
				<DetailRow label="Trvání" value={item.trvani} />
				<DetailRow label="Citlivost" value={item.citlivost} />
				<DetailRow label="Závažnost" value={item.zavaznost} />
				<DetailRow label="Léky proti bolesti" value={item.leky} />
				<DetailRow label="Poznámka" value={item.poznamka} />
				{!hasDetails && <NoDetails />}
			</div>
			<DoctorNotes notes={item.notes} />
			<StatusActions kind="acute" id={item.id} status={item.status}>
				<TranscriptButton
					voiceCallId={item.voiceCallId}
					hasTranscript={item.hasTranscript}
					onShow={onShowTranscript}
				/>
			</StatusActions>
		</div>
	)
}

function ChangeRequestItem({
	item,
	onShowTranscript,
}: {
	item: ChangeRequest
	onShowTranscript: ShowTranscript
}) {
	return (
		<div className={`${portalPanel} p-5`}>
			<ItemHeader
				title={`${item.jmeno} ${item.prijmeni}`}
				status={item.status}
				tag={item.typ}
			/>
			<ItemMeta
				telefon={item.telefon}
				createdAt={item.createdAt}
				patient={item.patient}
			/>
			<div className="mt-2 space-y-0.5">
				<DetailRow label="Původní termín" value={item.puvodniTermin} />
				<DetailRow label="Preferovaný termín" value={item.preferovanyTermin} />
				<DetailRow label="Poznámka" value={item.poznamka} />
				{!item.puvodniTermin && !item.preferovanyTermin && !item.poznamka && (
					<NoDetails />
				)}
			</div>
			<StatusActions kind="change" id={item.id} status={item.status}>
				<TranscriptButton
					voiceCallId={item.voiceCallId}
					hasTranscript={item.hasTranscript}
					onShow={onShowTranscript}
				/>
			</StatusActions>
		</div>
	)
}

type StatusFilter = 'all' | VoiceStatus

const FILTERS: { value: StatusFilter; label: string }[] = [
	{ value: 'all', label: 'Vše' },
	{ value: 'new', label: STATUS_LABELS.new },
	{ value: 'in_progress', label: STATUS_LABELS.in_progress },
	{ value: 'done', label: STATUS_LABELS.done },
]

function FilterChip({
	label,
	count,
	active,
	onClick,
}: {
	label: string
	count: number
	active: boolean
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-none border px-3 py-1.5 text-[12px] uppercase tracking-[1px] transition-colors ${
				active
					? 'border-[#d7ad6c] bg-[#d7ad6c] text-white'
					: 'border-[#dedede] bg-white text-[#242424] hover:border-[#d7ad6c]'
			}`}
		>
			{label} ({count})
		</button>
	)
}

/**
 * One tab's content: a status filter-chip row and one card per ticket.
 * "Vše" keeps finished tickets at the bottom so open work stays on top.
 */
function VoiceSection<T extends { id: string; status: VoiceStatus }>({
	items,
	renderItem,
	emptyText,
}: {
	items: T[]
	renderItem: (item: T) => React.ReactNode
	emptyText: string
}) {
	const [filter, setFilter] = useState<StatusFilter>('all')

	const countFor = (f: StatusFilter) =>
		f === 'all' ? items.length : items.filter((i) => i.status === f).length

	// "Vše" is ordered Nové → Vyřizuje se → Hotovo, so new tickets always
	// open at the top of the screen (server order kept within each group).
	const visible =
		filter === 'all'
			? (['new', 'in_progress', 'done'] as const).flatMap((status) =>
					items.filter((i) => i.status === status),
				)
			: items.filter((i) => i.status === filter)

	return (
		<div>
			<div className="mb-4 flex flex-wrap gap-2">
				{FILTERS.map((f) => (
					<FilterChip
						key={f.value}
						label={f.label}
						count={countFor(f.value)}
						active={filter === f.value}
						onClick={() => setFilter(f.value)}
					/>
				))}
			</div>
			{visible.length === 0 ? (
				<div className={`${portalPanel} p-6 text-base`}>{emptyText}</div>
			) : (
				<div className="space-y-4">{visible.map(renderItem)}</div>
			)}
		</div>
	)
}

/** Prague calendar day as YYYY-MM-DD (the server's day boundary). */
function pragueToday(): string {
	return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' })
}

function shiftDay(day: string, delta: number): string {
	const [y, m, d] = day.split('-').map(Number)
	const date = new Date(Date.UTC(y, m - 1, d + delta))
	return date.toISOString().slice(0, 10)
}

function formatCallTime(iso: string): string {
	return new Date(iso).toLocaleTimeString('cs-CZ', {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'Europe/Prague',
	})
}

function CallRow({
	call,
	onShowTranscript,
}: {
	call: CallItem
	onShowTranscript: ShowTranscript
}) {
	const DirectionIcon =
		call.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming
	const duration = formatDuration(call.durationSec)
	return (
		<div className={`${portalPanel} flex flex-wrap items-start gap-3 p-4`}>
			<DirectionIcon
				className="mt-1 h-5 w-5 shrink-0 text-[#d7ad6c]"
				aria-label={call.direction === 'outbound' ? 'Odchozí' : 'Příchozí'}
			/>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<span className="font-semibold">
						{formatCallTime(call.startedAt)}
					</span>
					{duration && <span className="text-[#919194]">{duration}</span>}
					{call.patient ? (
						<Link
							className="underline underline-offset-2 hover:text-[#d7ad6c]"
							to={`/patient/${call.patient.id}`}
						>
							{call.patient.firstName} {call.patient.lastName}
						</Link>
					) : null}
					<span className="text-[#919194]">
						{call.phone ? formatPhone(call.phone) : 'Skryté číslo'}
					</span>
					{call.acuteCount > 0 && (
						<span className="border border-red-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[1px] text-red-600">
							Akutní
						</span>
					)}
					{call.changeCount > 0 && (
						<span className="border border-[#d7ad6c] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[1px] text-[#d7ad6c]">
							Požadavek
						</span>
					)}
				</div>
				{(call.outcome || call.summary) && (
					<p className="mt-1 text-base">{call.outcome ?? call.summary}</p>
				)}
				{call.status && (
					<p className="text-[12px] text-[#919194]">Stav: {call.status}</p>
				)}
			</div>
			<TranscriptButton
				voiceCallId={call.voiceCallId}
				hasTranscript={call.hasTranscript}
				onShow={onShowTranscript}
			/>
		</div>
	)
}

/** One Prague day of calls, newest first, paged by "Načíst další". */
function CallsSection({
	onShowTranscript,
}: {
	onShowTranscript: ShowTranscript
}) {
	const [day, setDay] = useState(pragueToday)
	const today = pragueToday()
	const query = trpc.voiceAgent.calls.list.useInfiniteQuery(
		{ date: day },
		{
			getNextPageParam: (last) => last.nextOffset ?? undefined,
			initialCursor: 0,
			staleTime: 30000,
			refetchOnWindowFocus: true,
		},
	)
	const items = query.data?.pages.flatMap((p) => p.items) ?? []

	return (
		<div>
			<div className="mb-4 flex items-center justify-between gap-2">
				<Button
					className={`${portalButtonOutline} h-8 px-2`}
					size="sm"
					aria-label="Předchozí den"
					onClick={() => setDay((d) => shiftDay(d, -1))}
				>
					<ChevronLeft className="h-4 w-4" />
				</Button>
				<span className="text-base font-semibold">
					{day === today
						? 'Dnes'
						: formatDateCzech(new Date(`${day}T12:00:00`), 'd. M. yyyy')}
				</span>
				<Button
					className={`${portalButtonOutline} h-8 px-2`}
					size="sm"
					aria-label="Další den"
					disabled={day >= today}
					onClick={() => setDay((d) => shiftDay(d, 1))}
				>
					<ChevronRight className="h-4 w-4" />
				</Button>
			</div>

			{query.isLoading ? (
				<div className={`${portalPanel} p-6 text-base`}>Načítám hovory…</div>
			) : query.isError ? (
				<div className={`${portalPanel} space-y-3 p-6 text-base`}>
					<p>Hovory se nepodařilo načíst.</p>
					<Button
						className={portalButtonOutline}
						onClick={() => query.refetch()}
					>
						Zkusit znovu
					</Button>
				</div>
			) : items.length === 0 ? (
				<div className={`${portalPanel} p-6 text-base`}>
					Žádné hovory v tento den.
				</div>
			) : (
				<div className="space-y-3">
					{items.map((call) => (
						<CallRow
							key={call.voiceCallId}
							call={call}
							onShowTranscript={onShowTranscript}
						/>
					))}
					{query.hasNextPage && (
						<Button
							className={`${portalButtonOutline} w-full`}
							disabled={query.isFetchingNextPage}
							onClick={() => query.fetchNextPage()}
						>
							Načíst další
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

export function VoiceAgentPage() {
	const [transcriptCallId, setTranscriptCallId] = useState<string | null>(null)
	const { data, isLoading, isError, refetch } = trpc.voiceAgent.list.useQuery(
		undefined,
		{ staleTime: 30000, refetchOnWindowFocus: true },
	)

	if (isLoading) {
		return <PortalLoading />
	}

	// A failed query must never render as "no records" — an urgent case
	// hidden by an outage is exactly the record staff must not miss
	if (isError || !data) {
		return (
			<PortalCentered>
				<div
					className={`${portalPanel} w-full max-w-md space-y-4 p-8 text-center`}
				>
					<p className="text-[#242424]">
						Záznamy se nepodařilo načíst. Zkontrolujte připojení a zkuste to
						prosím znovu.
					</p>
					<Button className={portalButtonOutline} onClick={() => refetch()}>
						Zkusit znovu
					</Button>
				</div>
			</PortalCentered>
		)
	}

	return (
		<PortalShell>
			<header className="border-b border-[#dedede] bg-white">
				<div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
					{/* Text rendition of the site logo */}
					<div className="flex items-center gap-3">
						<p className={`${portalHeading} text-2xl leading-none`}>Josenika</p>
						<span className={`${portalLabel} text-sm`}>Interní systém</span>
					</div>
					<Button asChild className={portalButtonOutline} size="sm">
						<Link to="/">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Zpět na přehled
						</Link>
					</Button>
				</div>
			</header>

			<main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
				<div className="mb-10 text-center">
					<span className={portalLabel}>Hlasový agent</span>
					<h1
						className={`${portalHeading} mt-2 text-[28px] leading-tight sm:text-[34px]`}
					>
						Záznamy z telefonních hovorů
					</h1>
					<div className="mx-auto mt-4 h-[2px] w-[55px] bg-[#d7ad6c]" />
				</div>

				<Tabs defaultValue="acute">
					<TabsList className="grid h-auto w-full grid-cols-3 gap-0 rounded-none bg-transparent p-0">
						<TabsTrigger className={voiceTab} value="acute">
							Akutní případy
							<TabCount count={openCount(data.acuteCases)} />
						</TabsTrigger>
						<TabsTrigger className={voiceTab} value="requests">
							Požadavky a zprávy
							<TabCount count={openCount(data.changeRequests)} />
						</TabsTrigger>
						<TabsTrigger className={voiceTab} value="calls">
							Hovory
						</TabsTrigger>
					</TabsList>
					<TabsContent className="mt-4" value="acute">
						<VoiceSection
							items={data.acuteCases}
							renderItem={(item) => (
								<AcuteCaseItem
									key={item.id}
									item={item}
									onShowTranscript={setTranscriptCallId}
								/>
							)}
							emptyText="Žádné akutní případy k vyřízení."
						/>
					</TabsContent>
					<TabsContent className="mt-4" value="requests">
						<VoiceSection
							items={data.changeRequests}
							renderItem={(item) => (
								<ChangeRequestItem
									key={item.id}
									item={item}
									onShowTranscript={setTranscriptCallId}
								/>
							)}
							emptyText="Žádné požadavky k vyřízení."
						/>
					</TabsContent>
					<TabsContent className="mt-4" value="calls">
						<CallsSection onShowTranscript={setTranscriptCallId} />
					</TabsContent>
				</Tabs>
				<CallTranscriptDialog
					voiceCallId={transcriptCallId}
					onOpenChange={(open) => {
						if (!open) setTranscriptCallId(null)
					}}
				/>
			</main>
		</PortalShell>
	)
}
